// src/constants/niches.ts
// Drives the niche sub-portfolios. One config object per specialist site.
// The main site (mordecai.web.app) renders the "full" generalist profile.
//
// How a niche is resolved at runtime (see hooks/useNiche.ts):
//   1. ?niche=<key> query param  (for previewing any niche on any host)
//   2. hostname contains one of `hostKeys` (the deployed Firebase sites)
//   3. otherwise -> "full"
//
// Adding a new niche later = add one entry here + one Firebase hosting target.

import { About, Project, Service, Skill } from "../types";

export type NicheKey =
  | "full"
  | "ai"
  | "mobile"
  | "web"
  | "saas"
  | "games"
  | "edge";

export interface NicheConfig {
  key: NicheKey;
  /** hostname fragments that map to this niche (Firebase site ids) */
  hostKeys: string[];
  /** short specialist label shown as a chip in the nav/hero */
  label: string;
  /** the "Focus" line on the hero ID card */
  focus: string;
  /** hero headline; wrap a word in *asterisks* to italicise it in the accent colour */
  headline: string;
  /** hero subtitle — overrides about.title */
  heroRole: string;
  /** one-line punch under the hero subtitle */
  tagline: string;
  /** niche-specific bio — overrides about.bio */
  bio: string;
  /** schema.org Person jobTitle for this niche */
  jobTitle: string;
  /** project category names to include (case-insensitive) */
  projectCategories: string[];
  /** cross-category showcase projects to ALSO include, in priority order (by title) */
  featuredTitles: string[];
  /** service titles to show, in order (others hidden) */
  serviceTitles: string[];
  /** skill category fragments to prioritise (falls back to all if too few) */
  skillCategories: string[];
  /** per-niche SEO */
  seo: { title: string; description: string; keywords: string; image?: string };
}

const FULL: NicheConfig = {
  key: "full",
  hostKeys: [],
  label: "",
  focus: "Flutter · LLM · RAG · Vision",
  headline: "Mobile, web & AI — *shipped* end-to-end.",
  heroRole: "AI & Full-Stack Software Engineer",
  tagline:
    "Six years building across the stack — Flutter apps on both stores, React platforms, and production AI — for clients in Japan, South Korea, the UAE and Nigeria.",
  bio: "",
  jobTitle: "AI & Full-Stack Software Engineer",
  projectCategories: [],
  featuredTitles: [],
  serviceTitles: [],
  skillCategories: [],
  seo: {
    title: "Mordecai Shekwogaza | AI & Full-Stack Software Engineer",
    description:
      "AI & Full-Stack Software Engineer with 40+ shipped projects across mobile, web, and AI. Flutter, React, Firebase, LLMs, RAG, computer vision. Founder of Artisan Studio.",
    keywords:
      "Mordecai Shekwogaza, AI Engineer, Full Stack Developer, Flutter, React, Firebase, hire software engineer Nigeria",
  },
};

export const NICHES: Record<NicheKey, NicheConfig> = {
  full: FULL,

  ai: {
    key: "ai",
    hostKeys: ["mordecai-ai"],
    label: "AI Engineer",
    focus: "LLM · RAG · Vision · Voice",
    headline: "Production AI, *shipped* end-to-end.",
    heroRole: "AI Engineer — LLMs, RAG, Computer Vision & Voice",
    tagline:
      "I build and ship production AI: RAG chatbots, fine-tuned models, real-time vision, and voice pipelines.",
    bio:
      "AI Engineer who ships production-grade AI systems end to end. I build RAG-powered chatbots grounded in business data (Respondiq — a live AI SaaS), real-time computer vision with YOLO, offline voice pipelines with Whisper + TTS, autonomous tool-using agents, and fine-tuned PyTorch models — then deploy them, from cloud to the edge with ONNX quantization.\n\nMy AI work is already in production: SaveHayat uses Google Cloud Vision + a custom TensorFlow model for AI food-freshness prediction across 14 countries, and Respondiq turns any company's documents into an embeddable, multi-model chatbot (Claude, Ollama) with streaming answers and usage-based billing.\n\nI combine deep model/AI-systems knowledge with full-stack delivery (FastAPI, React, Firebase, Stripe) — so I don't just prototype models, I turn them into products people pay for.",
    jobTitle: "AI Engineer",
    projectCategories: ["AI"],
    featuredTitles: [
      "Respondiq",
      "SaveHayat",
      "AI Vision  ",
      "RAG Knowledge System",
      "YOLO Security Camera",
      "Edge AI Pipeline",
      "AI Agent System",
      "AI Voice Assistant",
      "Sentiment Classifier",
      "AI SaaS Platform",
    ],
    serviceTitles: [
      "AI SaaS Product Development",
      "AI Chatbot & Knowledge Systems",
      "Computer Vision Solutions",
      "Voice AI & Speech Processing",
    ],
    skillCategories: ["AI & Machine Learning", "Backend & APIs", "Languages & Frameworks"],
    seo: {
      title: "Mordecai Shekwogaza | AI Engineer — LLM, RAG, Computer Vision",
      description:
        "AI Engineer building production AI: RAG chatbots, LLM apps (Claude/Ollama), computer vision (YOLO), voice AI (Whisper), fine-tuned models, and edge ML. Hire an AI engineer.",
      keywords:
        "AI engineer, LLM developer, RAG chatbot developer, machine learning engineer, computer vision developer, Claude API developer, AI engineer Nigeria, hire AI engineer",
      image: "https://mordecai.web.app/og-ai.png",
    },
  },

  mobile: {
    key: "mobile",
    hostKeys: ["mordecai-mobile"],
    label: "Flutter Developer",
    focus: "Flutter · Riverpod · Firebase · iOS/Android",
    headline: "Cross-platform apps, *shipped* to both stores.",
    heroRole: "Flutter & Cross-Platform Mobile Engineer",
    tagline:
      "10+ apps shipped to the App Store & Play Store — from MVP to production, one codebase, both platforms.",
    bio:
      "Cross-platform mobile engineer specializing in Flutter & Dart. I take apps from idea to the App Store and Play Store — with clean UI/UX, real-time data, push notifications, payments, and in-app purchases.\n\nI've shipped published apps across health (NutriLens Pro, AI Vision — both live on the App Store), commerce (Commerce Hub), esports (CrownZone), news + chat (Pigeon), and utilities (Score Keep). For SaveHayat I led a full-scale Flutter + Firebase + Riverpod food-waste platform serving 14+ countries with multi-provider delivery, Stripe/Apple Pay/Google Pay, and Arabic RTL localization.\n\nFirebase, Riverpod/Provider/GetX, native integrations, offline-first storage, and store deployment are second nature — I deliver polished apps that feel native on both platforms.",
    jobTitle: "Mobile App Developer (Flutter)",
    projectCategories: ["Mobile App"],
    featuredTitles: [
      "SaveHayat",
      "AI Vision  ",
      "NutriLens Pro",
      "CrownZone",
      "Pigeon - Turkish News App",
      "Commerce Hub",
      "Score Keep",
      "Adventure Realty",
    ],
    serviceTitles: ["Mobile App Development"],
    skillCategories: ["Mobile & State Management", "Languages & Frameworks", "Backend & APIs"],
    seo: {
      title: "Mordecai Shekwogaza | Flutter & Mobile App Developer",
      description:
        "Flutter developer with 10+ iOS & Android apps shipped to the App Store and Play Store. Cross-platform apps with Firebase, payments, real-time data. Hire a Flutter developer.",
      keywords:
        "Flutter developer, mobile app developer, iOS Android developer, cross-platform developer, Dart developer, Firebase developer, Flutter developer Nigeria, hire Flutter developer",
      image: "https://mordecai.web.app/og-mobile.png",
    },
  },

  web: {
    key: "web",
    hostKeys: ["mordecai-web"],
    label: "Web Developer",
    focus: "React · TypeScript · Django · Flask",
    headline: "Web platforms, *built* to last.",
    heroRole: "Full-Stack Web Developer",
    tagline:
      "I build complete, production-ready web products — robust back-end APIs and databases paired with fast, modern front-ends.",
    bio:
      "Full-stack web engineer who ships complete web products end to end. On the backend I build robust, well-documented REST APIs and scalable services in Python — with Django, FastAPI, and Flask — backed by PostgreSQL and Firebase, including authentication, payments, and cloud deployment. On the frontend I build fast, modern interfaces with React and TypeScript.\n\nSelected work: Respondiq (a FastAPI + React AI SaaS with REST APIs, auth, and Stripe billing), Come On (a Flask-backed tennis tournament platform for a South Korean startup), and the official condominium sales website for a major Japanese developer (阪急阪神不動産). I also build the React admin CMS and APIs that power data-driven products.\n\nFrom database schema and API design to a polished UI and deployment, I deliver web apps that are clean, scalable, and production-ready.",
    jobTitle: "Full-Stack Web Developer",
    projectCategories: ["Web App"],
    featuredTitles: [
      "Come On - Web",
      "ジオ横浜大通り公園 — Condominium Sales Website",
      "Respondiq",
      "AI SaaS Platform",
    ],
    serviceTitles: [
      "Web Application Development",
      "AI SaaS Product Development",
      "AI Chatbot & Knowledge Systems",
    ],
    skillCategories: ["Languages & Frameworks", "Backend & APIs", "Databases & Storage"],
    seo: {
      title: "Mordecai Shekwogaza | Full-Stack Web Developer",
      description:
        "Full-stack web developer building Python/Django & FastAPI REST APIs with PostgreSQL, plus modern React front-ends. Scalable, production-ready web apps. Hire a Django/full-stack developer.",
      keywords:
        "Django developer, Python developer, REST API developer, PostgreSQL developer, full stack developer, FastAPI, React developer, Flask developer, web developer Nigeria, hire Django developer",
      image: "https://mordecai.web.app/og-web.png",
    },
  },

  saas: {
    key: "saas",
    hostKeys: ["mordecai-saas"],
    label: "AI SaaS Engineer",
    focus: "LLM · Stripe · Auth · Streaming",
    headline: "AI SaaS, *launched* end-to-end.",
    heroRole: "AI SaaS Product Engineer — From Idea to Launch",
    tagline:
      "I ship complete AI SaaS products: auth, RAG, streaming, usage limits, Stripe billing, and deploy.",
    bio:
      "I build complete AI SaaS products end to end — not prototypes. Auth, multi-tenant data, LLM integration, RAG pipelines, real-time streaming, usage-based limits, Stripe billing, analytics, and scalable cloud deployment.\n\nRespondiq is my flagship: a live, white-label AI chatbot builder where business owners upload documents and embed a custom, RAG-powered chatbot with one script tag — multi-model (Claude, Ollama), streaming answers, widget customization, conversation history, and tiered Stripe pricing. I also architected SaveHayat, a production multi-platform marketplace with AI, payments, and compliance across 14 countries.\n\nAs a founder (Artisan Studio) and engineer, I think in products and revenue, not just code — I help you go from idea to a launched, monetizable AI SaaS.",
    jobTitle: "AI SaaS Product Engineer",
    projectCategories: ["AI"],
    featuredTitles: [
      "Respondiq",
      "AI SaaS Platform",
      "SaveHayat",
      "RAG Knowledge System",
    ],
    serviceTitles: [
      "AI SaaS Product Development",
      "AI Chatbot & Knowledge Systems",
      "Web Application Development",
    ],
    skillCategories: [
      "AI & Machine Learning",
      "Backend & APIs",
      "Payments & Monetization",
      "Databases & Storage",
    ],
    seo: {
      title: "Mordecai Shekwogaza | AI SaaS Product Engineer",
      description:
        "AI SaaS product engineer who ships end-to-end: LLM/RAG features, auth, streaming, usage limits, Stripe billing, and deployment. Founder of Artisan Studio. Build your AI SaaS.",
      keywords:
        "AI SaaS developer, SaaS product engineer, LLM SaaS, RAG SaaS, Stripe integration, MVP developer, AI product developer, build AI SaaS, hire SaaS engineer",
      image: "https://mordecai.web.app/og-saas.png",
    },
  },

  games: {
    key: "games",
    hostKeys: ["mordecai-games"],
    label: "Game & Esports Dev",
    focus: "Flutter · Realtime · Tournaments",
    headline: "Tournament apps, *built* for real players.",
    heroRole: "Game & Esports App Developer",
    tagline:
      "Real-time multiplayer, tournaments, leaderboards, wallets & rewards — gaming apps that scale.",
    bio:
      "I build gaming and esports applications with real-time multiplayer mechanics — tournaments, brackets, live scoring, leaderboards, wallets, rewards, and in-app purchases.\n\nCrownZone is a cross-platform esports tournament platform (tournament discovery, match participation, coin/wallet system, referrals, FCM push) built with Flutter + Firebase. eSport ships full tournament logistics — knockout and round-robin formats, registration, and live bracket updates with real-time Firebase sync. Come On digitizes the full lifecycle of competitive tennis tournaments, and Score Keep is a polished, offline-first game score tracker on the App Store.\n\nFlutter, Firebase real-time sync, Hive, FCM, and in-app purchases let me deliver responsive, engaging game experiences across Android and iOS.",
    jobTitle: "Game & Esports App Developer",
    projectCategories: ["Game", "Esports"],
    featuredTitles: ["CrownZone", "eSport", "Come On - Web", "Score Keep"],
    serviceTitles: ["Mobile App Development"],
    skillCategories: ["Mobile & State Management", "Languages & Frameworks"],
    seo: {
      title: "Mordecai Shekwogaza | Game & Esports App Developer",
      description:
        "Game & esports app developer building real-time multiplayer tournaments, brackets, leaderboards, wallets, and rewards with Flutter & Firebase. Hire a game app developer.",
      keywords:
        "game developer, esports app developer, tournament app developer, multiplayer app, Flutter game developer, mobile game developer, leaderboard app, hire game developer",
      image: "https://mordecai.web.app/og-games.png",
    },
  },

  edge: {
    key: "edge",
    hostKeys: ["mordecai-edge"],
    label: "Edge AI Engineer",
    focus: "ONNX · YOLO · Ollama · On-device",
    headline: "AI that runs *on the device*.",
    heroRole: "Edge AI & On-Device ML Engineer",
    tagline:
      "On-device, offline-capable ML — real-time vision, local LLMs, and quantized models for the edge.",
    bio:
      "Edge AI engineer focused on running ML where the data is — on devices, offline, and in real time. I optimize and deploy models for constrained hardware: ONNX export, INT8 quantization (up to 4× smaller, <2% accuracy loss), and inference benchmarking from Raspberry Pi to the browser.\n\nMy edge work includes a real-time YOLOv8 security-camera pipeline (30+ FPS), a fully offline voice assistant (Whisper + local Ollama + TTS, sub-3s responses, no cloud), custom-trained PyTorch models, and an end-to-end PyTorch→ONNX→quantized deployment pipeline. I run and fine-tune local models on my own NPU + RTX hardware.\n\nIf you need AI that's private, low-latency, and works without a network connection, that's exactly what I build.",
    jobTitle: "Edge AI / On-Device ML Engineer",
    projectCategories: ["AI"],
    featuredTitles: [
      "Edge AI Pipeline",
      "YOLO Security Camera",
      "AI Voice Assistant",
      "Sentiment Classifier",
      "RAG Knowledge System",
    ],
    serviceTitles: [
      "Computer Vision Solutions",
      "Voice AI & Speech Processing",
      "AI SaaS Product Development",
    ],
    skillCategories: ["AI & Machine Learning", "Languages & Frameworks"],
    seo: {
      title: "Mordecai Shekwogaza | Edge AI & On-Device ML Engineer",
      description:
        "Edge AI engineer deploying on-device, offline ML: real-time YOLO vision, local LLMs (Ollama), Whisper voice, and ONNX-quantized models for edge hardware. Hire an edge AI engineer.",
      keywords:
        "edge AI engineer, on-device ML, ONNX quantization, embedded AI, offline AI, local LLM developer, YOLO developer, TinyML, edge computing, hire edge AI engineer",
      image: "https://mordecai.web.app/og-edge.png",
    },
  },
};

const norm = (s: string) => s.trim().toLowerCase();

/** Resolve the active niche key from hostname + query string. */
export function resolveNicheKey(hostname: string, search: string): NicheKey {
  try {
    const q = new URLSearchParams(search).get("niche");
    if (q && q in NICHES) return q as NicheKey;
  } catch {
    /* ignore */
  }
  const host = (hostname || "").toLowerCase();
  for (const n of Object.values(NICHES)) {
    if (n.key === "full") continue;
    if (n.hostKeys.some((h) => host.includes(h))) return n.key;
  }
  return "full";
}

/** Projects for a niche: category matches + curated cross-category showcases, prioritized. */
export function nicheProjects(all: Project[], niche: NicheConfig): Project[] {
  if (niche.key === "full") return all;
  const cats = niche.projectCategories.map(norm);
  const titles = niche.featuredTitles.map(norm);
  const matched = all.filter((p) => {
    const pc = (p.category || "").split(",").map(norm);
    return pc.some((c) => cats.includes(c)) || titles.includes(norm(p.title));
  });
  const rank = (p: Project) => {
    const i = titles.indexOf(norm(p.title));
    return i === -1 ? 999 : i;
  };
  return matched.sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    // then newest first
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

/** Services for a niche, in configured order. Empty config => all services. */
export function nicheServices(all: Service[], niche: NicheConfig): Service[] {
  if (niche.key === "full" || niche.serviceTitles.length === 0) return all;
  const order = niche.serviceTitles.map(norm);
  const picked = all.filter((s) => order.includes(norm(s.title)));
  picked.sort((a, b) => order.indexOf(norm(a.title)) - order.indexOf(norm(b.title)));
  return picked.length ? picked : all;
}

/** Skills for a niche. Soft filter — falls back to all if the niche slice is thin (<6). */
export function nicheSkills(all: Skill[], niche: NicheConfig): Skill[] {
  if (niche.key === "full" || niche.skillCategories.length === 0) return all;
  const wanted = niche.skillCategories.map(norm);
  const picked = all.filter((s) =>
    wanted.some((w) => norm(s.category || "").includes(w))
  );
  return picked.length >= 6 ? picked : all;
}

/** Apply niche overrides to the About object (hero subtitle + bio + niche-specific resume). */
export function applyNicheAbout(about: About | null, niche: NicheConfig): About | null {
  if (!about || niche.key === "full") return about;
  const nicheResume = about.nicheResumes?.[niche.key];
  return {
    ...about,
    title: niche.heroRole,
    bio: niche.bio,
    resume: nicheResume || about.resume,
  };
}

/* ==================== ADMIN-EDITABLE OVERRIDES ====================
   The configs above are the shipped defaults/fallbacks. The admin "Niche Sites"
   tab writes partial overrides to the Firestore `niches` collection (one doc per
   niche key); anything absent falls back to the code value, so a missing or
   partial doc can never break a site. */

export interface NicheOverride {
  label?: string;
  focus?: string;
  headline?: string;
  heroRole?: string;
  tagline?: string;
  bio?: string;
  jobTitle?: string;
  projectCategories?: string[];
  featuredTitles?: string[];
  serviceTitles?: string[];
  skillCategories?: string[];
  seo?: Partial<NicheConfig["seo"]>;
}

/** The fields the admin tab exposes, in display order. */
export const NICHE_EDITABLE_KEYS: NicheKey[] = [
  "full",
  "ai",
  "mobile",
  "web",
  "saas",
  "games",
  "edge",
];

/** Merge a Firestore override onto a code config. Blank strings are ignored. */
export function mergeNiche(base: NicheConfig, override?: NicheOverride): NicheConfig {
  if (!override) return base;
  const str = (v: string | undefined, fallback: string) =>
    typeof v === "string" && v.trim() ? v : fallback;
  const arr = (v: string[] | undefined, fallback: string[]) =>
    Array.isArray(v) ? v.filter((s) => s.trim()) : fallback;

  return {
    ...base,
    label: str(override.label, base.label),
    focus: str(override.focus, base.focus),
    headline: str(override.headline, base.headline),
    heroRole: str(override.heroRole, base.heroRole),
    tagline: str(override.tagline, base.tagline),
    bio: str(override.bio, base.bio),
    jobTitle: str(override.jobTitle, base.jobTitle),
    projectCategories: arr(override.projectCategories, base.projectCategories),
    featuredTitles: arr(override.featuredTitles, base.featuredTitles),
    serviceTitles: arr(override.serviceTitles, base.serviceTitles),
    skillCategories: arr(override.skillCategories, base.skillCategories),
    seo: {
      title: str(override.seo?.title, base.seo.title),
      description: str(override.seo?.description, base.seo.description),
      keywords: str(override.seo?.keywords, base.seo.keywords),
      image: str(override.seo?.image, base.seo.image || ""),
    },
  };
}
